import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "cb-cookie-consent";

const readInitialShow = () => {
  try {
    return !window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return true;
  }
};

const CookieBanner = () => {
  const [show, setShow] = useState(readInitialShow);

  const persist = (value) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, at: new Date().toISOString() })
      );
    } catch {
      /* ignore */
    }
    // Update Google Consent Mode v2 + send the deferred initial page_view
    try {
      if (typeof window.gtag === "function") {
        const granted = value === "accepted-all";
        window.gtag("consent", "update", {
          analytics_storage: granted ? "granted" : "denied",
        });
        if (granted) {
          window.gtag("event", "page_view", {
            page_path: window.location.pathname + window.location.search,
            page_location: window.location.href,
            page_title: document.title,
          });
        }
      }
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-50"
      data-testid="cookie-banner"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="bg-white border border-black/10 rounded-md shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18)] p-5 md:p-6">
        <div className="flex items-start gap-3 mb-4">
          <Cookie className="h-5 w-5 text-accent mt-0.5 shrink-0" />
          <div>
            <div className="font-display font-semibold text-[1rem] tracking-tight text-ink">
              We use a few cookies.
            </div>
            <p className="text-[13px] text-muted-foreground leading-relaxed mt-1">
              Strictly necessary cookies keep this site working. We&rsquo;d also like to set
              optional analytics cookies to understand how you use it. You can change your mind any time.
              See our{" "}
              <Link to="/cookies" className="text-brand underline hover:text-accent">
                Cookies Policy
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-brand underline hover:text-accent">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          <button
            onClick={() => persist("declined")}
            aria-label="Close"
            className="-mr-1 -mt-1 p-1 text-muted hover:text-ink"
            data-testid="cookie-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => persist("accepted-all")}
            data-testid="cookie-accept"
            className="inline-flex items-center px-4 py-2.5 bg-brand text-white text-[13px] font-semibold rounded-sm hover:bg-accent transition-colors"
          >
            Accept all
          </button>
          <button
            onClick={() => persist("necessary-only")}
            data-testid="cookie-necessary"
            className="inline-flex items-center px-4 py-2.5 border border-ink text-ink text-[13px] font-semibold rounded-sm hover:bg-ink hover:text-white transition-colors"
          >
            Necessary only
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
